import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { filterMovies, type SortKey } from "@/utils/movies";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新" },
  { key: "popular", label: "热门" },
  { key: "rating", label: "评分" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";

  const [input, setInput] = useState(initialQ);
  const [keyword, setKeyword] = useState(initialQ);
  const [typeFilter, setTypeFilter] = useState<"全部" | "电影" | "电视剧">("全部");
  const [sort, setSort] = useState<SortKey>("latest");

  useEffect(() => {
    const q = searchParams.get("q") || "";
    setInput(q);
    setKeyword(q);
  }, [searchParams]);

  const results = useMemo(
    () =>
      filterMovies({
        keyword,
        type: typeFilter === "电影" ? "movie" : typeFilter === "电视剧" ? "tv" : "",
        sort,
      }),
    [keyword, typeFilter, sort]
  );

  const runSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setKeyword(q);
    setSearchParams(q ? { q } : {});
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: "搜索结果" }]} />

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <form onSubmit={runSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-2 h-11 flex-1 px-4 rounded-md border border-background-300 bg-background-100 focus-within:border-primary-400 transition-colors">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-lg"></i>
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="搜索电影 / 电视剧名称、简介关键词"
              className="w-full bg-transparent text-sm font-label text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
          </div>
          <button
            type="submit"
            className="h-11 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-label font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </form>

        <div className="flex flex-wrap items-center gap-3 mt-4">
          <div className="flex items-center gap-1.5 p-1 rounded-full bg-background-100">
            {(["全部", "电影", "电视剧"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTypeFilter(t)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
                  typeFilter === t ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 p-1 rounded-full bg-background-100">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSort(opt.key)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-label transition-colors cursor-pointer whitespace-nowrap ${
                  sort === opt.key ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <p className="text-sm font-body text-foreground-500 mb-5">
          {keyword ? (
            <>
              关键词“<span className="text-foreground-900 font-medium">{keyword}</span>”共找到{" "}
              <span className="text-primary-600 font-semibold">{results.length}</span> 个结果
            </>
          ) : (
            <>
              输入关键词开始搜索，当前展示全部影视作品（
              <span className="text-primary-600 font-semibold">{results.length}</span> 个）
            </>
          )}
        </p>

        {results.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {results.map((movie) => (
              <MovieCard key={movie.id} movie={movie} showSynopsis />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 rounded-lg border border-dashed border-background-300 bg-background-50 text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-50 text-secondary-500 mb-4">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h2 className="text-base font-heading font-semibold text-foreground-900">
              无匹配内容，可尝试更换关键词或分类浏览
            </h2>
            <p className="mt-2 text-sm font-body text-foreground-500">
              换个关键词，或按分类快速找到想看的内容
            </p>
            <Link
              to="/category/movie"
              className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-apps-2-line text-base"></i>
              </span>
              浏览影视分类
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}